import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { Search, Plus, Minus, Trash2, User, Mail, Phone, MapPin, Building, Calendar, AlertTriangle, ShoppingCart, CreditCard, CheckCircle, Download, Receipt, Info, X, CalendarDaysIcon, MessageCircle } from 'lucide-react';
import { addOrders, getBusiness, getPosItems, getShippingAddresses, deleteCart } from "helpers/backend_helper";
import { getImageUrl } from "constants/constants";
import Select from 'react-select';
import { useCart, CartItem as CartItemType } from 'hooks/useCart';
import { useShare } from 'hooks/useShare';
import { AppContext } from "context/AppContext";
import { INVOICE_URL } from "constants/settings";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";

interface Business {
    id: number;
    businessName: string;
    gstNo: string;
    pan: string;
    ownerName: string;
    businessEstablishmentYear: number;
    businessType: string;
    addressLine1: string;
    addressLine2: string;
    landmark: string;
    city: string;
    district: string;
    state: string;
    country: string;
    pinCode: number;
    userId: number;
    userName: string;
    phone: string;
    email: string;
    status: string;
    isActive: boolean;
}

interface Product {
    variantId: number;
    productId: number;
    sku: string;
    productName: string;
    brandName: string;
    categoryName: string;
    sizeNames: string;
    colors: string[];
    unitPrice: string;
    packOf: number;
    discountPercent: string;
    discountValue: string;
    inventory: number;
}

interface CartItem extends Product {
    quantity: number;
}

interface Address {
    id: number;
    addressType: string;
    addressLine1: string;
    addressLine2: string;
    landmark: string;
    city: string;
    district: string;
    state: string;
    country: string;
    pinCode: string;

}

interface OrderDetails {
    id: number;
    orderId: string;
    orderDate: string;
    status: 'Processing' | 'Confirmed' | 'Shipped' | 'Delivered';
    items: CartItem[];
    shippingAddress: Address;
    businessDetails: Business;
    summary: {
        totalProducts: number;
        totalQuantity: number;
        subTotal: number;
        totalDiscount: number;
        gst: number;
        shipping: number;
        total: number;
    };
    filePath: string;
}

const POS = () => {
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<number>(0);
    const [selectedBusinessDetails, setSelectedBusinessDetails] = useState<Business | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("1");
    const [products, setProducts] = useState<Product[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedBusinessOption, setSelectedBusinessOption] = useState<any>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [pendingProduct, setPendingProduct] = useState<Product | null>(null);
    const [showCheckout, setShowCheckout] = useState<boolean>(false);
    const [checkoutStep, setCheckoutStep] = useState<number>(1);
    const [shippingCharge] = useState<number>(0);
    const [taxRate] = useState<number>(5);
    const [selectedAddress, setSelectedAddress] = useState<number | null>(null);
    const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSyncingCart, setIsSyncingCart] = useState<boolean>(false);
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [showBusinessDetails, setShowBusinessDetails] = useState<boolean>(false);
    const [isNextLoading, setIsNextLoading] = useState<boolean>(false);
    const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
    const [editingPriceValue, setEditingPriceValue] = useState<string>('');
    
    const { user } = useContext(AppContext);
    const commonHelper = new Common();
    const { shareOnWhatsApp } = useShare();

    const {
        cartItems,
        cartSummary,
        isLoading: isCartLoading,
        error: cartError,
        addItem,
        updateItem,
        updateItemPrice,
        removeItem,
        refreshCart,
        clearCart,
        syncCartWithBackend,
        calculateLocalSummary
    } = useCart(user?.userId ?? 0, selectedBusiness);

    // Memoize local summary calculation to avoid recalculating on every render
    const localSummary = useMemo(() => calculateLocalSummary(), [cartItems, calculateLocalSummary]);

    const addToCartButtonRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const quantityInputRef = useRef<HTMLInputElement>(null);

    // Fetch businesses on component mount
    useEffect(() => {
        fetchBusinesses();
        fetchPosItems();
        console.log(user);
    }, []);

    // Update useEffect to refresh cart when business changes
    useEffect(() => {
        if (selectedBusiness) {
            console.log("Refreshing cart:2");
            try{
                refreshCart();
            }
            catch(error){
                console.log(error);
            }
        }
    }, [selectedBusiness, refreshCart]);

    const loadShippingAddress = async () => {
        setIsLoading(true);

        try {
            const response = await getShippingAddresses(selectedBusiness);
            if (response.data && response.data.length > 0) {
                const firstAddress = response.data[0];
                setSelectedAddress(firstAddress.id);
                setAddresses(response.data);
                console.log('Shipping addresses loaded:', response.data);
            } else {
                setAddresses([]);
                setSelectedAddress(null);
                console.log('No shipping addresses found');
            }
        } catch (error) {
            console.error('Error loading shipping addresses:', error);
            setAddresses([]);
            setSelectedAddress(null);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchBusinesses = async () => {
        try {
            setIsLoading(true);
            const response = await getBusiness('Verified');
            if (response?.data) {
                setBusinesses(response.data);
            } else {
                setBusinesses([]);
            }
        } catch (error) {
            console.error("Error fetching businesses:", error);
            commonHelper.showErrorMessage("Failed to load businesses");
            setBusinesses([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPosItems = async () => {
        try {
            setIsLoading(true);
            const response = await getPosItems();
            if (response?.data) {
                setProducts(response.data);
            } else {
                setProducts([]);
            }
        } catch (error) {
            console.error("Error fetching POS items:", error);
            commonHelper.showErrorMessage("Failed to load products");
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async () => {
        if(!selectedBusiness){
            commonHelper.showErrorMessage("Please select a customer");
            return;
        }
        else if(searchQuery.trim().length <= 2){
            commonHelper.showErrorMessage("Please enter a valid sku#");
            return;
        }
        setIsSearching(true);
        try {
            const foundProduct = products.find(product =>
                product?.sku?.toUpperCase() === searchQuery.toUpperCase()
            );

                    if (foundProduct) {
            const quantityToAdd = parseInt(quantity) || 1;
            // Add the product with the specified quantity
            for (let i = 0; i < quantityToAdd; i++) {
                await addItem(foundProduct);
            }
            setSearchQuery('');
            setQuantity('1');
            // Focus back to the SKU scan textbox
            searchInputRef.current?.focus();
        } else {
            commonHelper.showErrorMessage("Product not found with SKU: " + searchQuery);
            // Focus back to the SKU scan textbox even if product not found
            searchInputRef.current?.focus();
        }
        } catch (error) {
            console.error("Error searching products:", error);
            commonHelper.showErrorMessage("Error searching products");
        } finally {
            setIsSearching(false);
        }
    };

    const handleConfirmAdd = async () => {
        if (pendingProduct) {
            try {
                await addItem(pendingProduct);
                setPendingProduct(null);
                setShowConfirmDialog(false);
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 100);
            } catch (error) {
                console.error("Error adding product:", error);
                commonHelper.showErrorMessage("Failed to add product to cart");
            }
        }
    };

    const handleCancelAdd = () => {
        setPendingProduct(null);
        setShowConfirmDialog(false);
    };

    const updateQuantity = async (productId: number, increment: boolean) => {
        const item = cartItems.find(item => item.variantId === productId);
        if (!item) return;

        const newQuantity = increment ? item.quantity + 1 : item.quantity - 1;
        if (newQuantity > 0) {
            try {
                await updateItem(productId, newQuantity);
            } catch (error) {
                console.error("Error updating quantity:", error);
                commonHelper.showErrorMessage("Failed to update quantity");
            }
        } else {
            try {
                await removeItem(productId);
            } catch (error) {
                console.error("Error removing item:", error);
                commonHelper.showErrorMessage("Failed to remove item");
            }
        }
    };

    const removeFromCart = async (variantId: number) => {
        try {
            const item = cartItems.find(item => item.variantId === variantId);
            if (item?.id && item.id > 0) {
                // If item has an ID > 0, delete from API first
                await deleteCart({ id: item.id });
            }
            // Remove from local cart state
            await removeItem(variantId);
        } catch (error) {
            console.error("Error removing item:", error);
            commonHelper.showErrorMessage("Failed to remove item");
        }
    };

    const handlePriceEdit = (variantId: number, currentPrice: string) => {
        setEditingPriceId(variantId);
        setEditingPriceValue(currentPrice);
    };

    const handlePriceSave = async (variantId: number) => {
        try {
            const newPrice = parseFloat(editingPriceValue);
            if (isNaN(newPrice) || newPrice < 0) {
                commonHelper.showErrorMessage("Please enter a valid price");
                return;
            }
            
            await updateItemPrice(variantId, editingPriceValue);
            setEditingPriceId(null);
            setEditingPriceValue('');
        } catch (error) {
            console.error("Error updating price:", error);
            commonHelper.showErrorMessage("Failed to update price");
        }
    };

    const handlePriceCancel = () => {
        setEditingPriceId(null);
        setEditingPriceValue('');
    };

    const handleCheckout = async () => {
        if (cartItems.length === 0) {
            
            commonHelper.showErrorMessage("Cart is empty. Add items to proceed with checkout.");
            return;
        }

        setIsSyncingCart(true);
        try {
            await syncCartWithBackend();
            setShowCheckout(true);
            setCheckoutStep(2);
            loadShippingAddress();
        } catch (error) {
            console.error("Error syncing cart:", error);
            commonHelper.showErrorMessage("Failed to sync cart with backend");
        } finally {
            setIsSyncingCart(false);
        }
    };

    const handleNextStep = async () => {
        if(checkoutStep === 1){
            setIsNextLoading(true);
            try {
                await handleCheckout();
            } finally {
                setIsNextLoading(false);
            }
        }
        else if (checkoutStep < 3) {
            setCheckoutStep(checkoutStep + 1);
        }
    };

    const handlePrevStep = () => {
        if (checkoutStep > 1) {
            setCheckoutStep(checkoutStep - 1);
        }
    };

    const handleSubmitOrder = async () => {
        if (!selectedAddress || !selectedBusinessDetails) return;
        
        setIsSubmitting(true);
        try {
            const data = {
                userId: user?.userId ?? 0,
                businessId: selectedBusiness,
                shippingAddressId: selectedAddress,
                couponCode: '',
            }
            console.log(data);
            const response = await addOrders(data);
            console.log(response);

            const order: OrderDetails = {
                id: response.data.id,
                orderId: response.data.orderNo,
                orderDate: new Date().toISOString(),
                status: 'Confirmed',
                items: cartItems,
                shippingAddress: addresses.find(a => a.id === selectedAddress)!,
                businessDetails: selectedBusinessDetails,
                summary: cartSummary??{totalProducts:0,totalQuantity:0,subTotal:0,totalDiscount:0,gst:0,shipping:0,total:0},
                filePath: response.data.filePath
            };
            
            setOrderDetails(order);
            setCheckoutStep(3);
            
            // Clear cart and reset state
            clearCart();
            setSelectedAddress(null);
        } catch (error) {
            console.error("Error placing order:", error);
            commonHelper.showErrorMessage("Failed to place order. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownloadInvoice = () => {
        if (orderDetails?.filePath) {
            const invoiceUrl = `${INVOICE_URL}${orderDetails.filePath}`;
            console.log('Downloading invoice from:', invoiceUrl);
            window.open(invoiceUrl, '_blank');
        }
    };

    const handleViewOrderDetails = () => {
        if (orderDetails?.orderId) {
            window.open(`/order-detail/${orderDetails.id}`, '_blank');
        }
    };

    const handleShareInvoiceOnWhatsApp = () => {
        if (orderDetails?.filePath && selectedBusinessDetails) {
            const invoiceUrl = `${INVOICE_URL}${orderDetails.filePath}`;
            const customerPhone = selectedBusinessDetails.phone;
            
            const message = `Hi ${selectedBusinessDetails.ownerName},

Your order has been confirmed! 📋

Order Details:
🛒 Order ID: ${orderDetails.orderId}
📅 Order Date: ${new Date(orderDetails.orderDate).toLocaleDateString()}
💰 Total Amount: ₹${orderDetails.summary.total.toFixed(2)}
🏢 Business: ${selectedBusinessDetails.businessName}

📄 Invoice: ${invoiceUrl}

Thank you for your business! 🙏`;
            
            shareOnWhatsApp(customerPhone, message);
        }
    };

    const handleNewOrder = () => {
        setShowCheckout(false);
        setCheckoutStep(1);
        clearCart();
        setSelectedAddress(null);
        setOrderDetails(null);
    };

    // Update business options for Select component
    const businessOptions = businesses.map(business => ({
        value: business.id,
        label: business.businessName
    }));

    // Update business change handler
    const handleBusinessChange = async (selectedOption: any) => {
        // Clear cart first
        clearCart();

        setSelectedBusinessOption(selectedOption);
        const businessId = selectedOption?.value || 0;
        setSelectedBusiness(businessId);
        const business = businesses.find(b => b.id === businessId);
        setSelectedBusinessDetails(business || null);
   };

    // Add keyboard event handler for the dialog
    useEffect(() => {
        const handleDialogKeyPress = (e: KeyboardEvent) => {
            if (showConfirmDialog && e.key === 'Enter') {
                e.preventDefault();
                handleConfirmAdd();
            }
        };

        if (showConfirmDialog) {
            document.addEventListener('keypress', handleDialogKeyPress);
            setTimeout(() => {
                addToCartButtonRef.current?.focus();
            }, 100);
        }

        return () => {
            document.removeEventListener('keypress', handleDialogKeyPress);
        };
    }, [showConfirmDialog]);

    // Add keyboard event handler for price editing
    useEffect(() => {
        const handlePriceKeyPress = (e: KeyboardEvent) => {
            if (editingPriceId && e.key === 'Escape') {
                e.preventDefault();
                handlePriceCancel();
            }
        };

        if (editingPriceId) {
            document.addEventListener('keydown', handlePriceKeyPress);
        }

        return () => {
            document.removeEventListener('keydown', handlePriceKeyPress);
        };
    }, [editingPriceId]);

    return (
        <React.Fragment>
        <ToastContainer closeButton={false} limit={1} />

            <div className="w-full px-1 mt-5">
                {/* Zero Inventory Confirmation Dialog */}
                {showConfirmDialog && pendingProduct && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-zink-700 rounded-lg p-6 max-w-md w-full mx-4">
                            <div className="flex items-center gap-3 mb-4">
                                <AlertTriangle className="h-6 w-6 text-yellow-500" />
                                <h3 className="text-lg font-semibold">Zero Inventory Warning</h3>
                            </div>
                            <p className="text-slate-600 dark:text-zink-200 mb-4">
                                This product ({pendingProduct.sku}) has zero inventory. Are you sure you want to add it to the cart?
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    className="btn bg-slate-200 hover:bg-slate-300 text-slate-700"
                                    onClick={handleCancelAdd}
                                >
                                    Cancel
                                </button>
                                <button
                                    ref={addToCartButtonRef}
                                    className="btn bg-custom-500 text-white focus:ring-2 focus:ring-custom-500 focus:ring-offset-2"
                                    onClick={handleConfirmAdd}
                                    autoFocus
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 mb-3">
                    {/* Business Selection and Product Search */}
                    <div className="card mb-0">
                        <div className="card-body p-0" style={{paddingTop: '5px', paddingBottom: '5px'}}>
                            <div className="flex items-center gap-4">
                                {/* Business Selection */}
                                <div className="flex-1 flex items-center gap-4">
                                    <div className="flex items-center gap-1">
                                        <span className="text-sm font-medium text-slate-600 dark:text-zink-200">Customer:</span>
                                        <div className="flex-1 min-w-[200px]">
                                            <Select
                                                 className="text-sm"
                                                classNamePrefix="select2-selection"
                                                placeholder="Select Business"
                                                options={businessOptions}
                                                value={selectedBusinessOption}
                                                onChange={handleBusinessChange}
                                                isDisabled={isLoading}
                                            />
                                        </div>
                                    </div>
                                    {selectedBusinessDetails && (
                                        <div className="flex items-center gap-6 text-sm">
                                            <div className="flex items-center gap-2">
                                                
                                                <User className="h-4 w-4 text-custom-500" />
                                                
                                                    <span className="font-medium">{selectedBusinessDetails.ownerName}</span>
                                                    <CalendarDaysIcon className="h-4 w-4 text-custom-500" />
                                            
                                                    <span className="text-slate-500 ml-2">Est. {selectedBusinessDetails.businessEstablishmentYear}</span>
                                                
                                                <MapPin className="h-4 w-4 text-custom-500" />
                                                
                                                    <span>{selectedBusinessDetails.city}, {selectedBusinessDetails.state}</span>
                                            
                                            </div>
                                            
                                            <button
                                                className="btn btn-sm bg-slate-200 hover:bg-slate-300 p-1"
                                                onClick={() => setShowBusinessDetails(true)}
                                            >
                                                <Info className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Business Details Popup */}
                    {showBusinessDetails && selectedBusinessDetails && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-zink-700 rounded-lg p-6 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg font-semibold">Business Details</h3>
                                    <button
                                        className="btn btn-sm bg-slate-200 hover:bg-slate-300 p-1"
                                        onClick={() => setShowBusinessDetails(false)}
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm text-slate-500">Business Name</label>
                                            <p className="font-medium">{selectedBusinessDetails.businessName}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Owner Name</label>
                                            <p className="font-medium">{selectedBusinessDetails.ownerName}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Business Type</label>
                                            <p className="font-medium">{selectedBusinessDetails.businessType}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Established Year</label>
                                            <p className="font-medium">{selectedBusinessDetails.businessEstablishmentYear}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm text-slate-500">Contact Information</label>
                                            <p className="font-medium">+91 {selectedBusinessDetails.phone}</p>
                                            <p className="font-medium">{selectedBusinessDetails.email}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Tax Information</label>
                                            <p className="font-medium">GST: {selectedBusinessDetails.gstNo}</p>
                                            <p className="font-medium">PAN: {selectedBusinessDetails.pan}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Address</label>
                                            <p className="font-medium">
                                                {selectedBusinessDetails.addressLine1}, {selectedBusinessDetails.addressLine2}
                                                {selectedBusinessDetails.landmark && `, ${selectedBusinessDetails.landmark}`}
                                            </p>
                                            <p className="font-medium">
                                                {selectedBusinessDetails.city}, {selectedBusinessDetails.district}, {selectedBusinessDetails.state} - {selectedBusinessDetails.pinCode}
                                            </p>
                                            <p className="font-medium">{selectedBusinessDetails.country}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cart and Checkout Wizard */}
                    <div className="bg-white dark:bg-zink-700 rounded-lg p-4">
                        <div className="grid grid-cols-12 gap-4">
                            {/* Wizard Content */}
                            <div className="col-span-9">
                                {/* Wizard Steps */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className={`flex items-center ${checkoutStep >= 1 ? (checkoutStep === 1 ? 'text-green-500 font-bold' : 'text-custom-500') : 'text-slate-400'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${checkoutStep >= 1 ? (checkoutStep === 1 ? 'border-green-500' : 'border-custom-500') : 'border-slate-400'}`}>
                                            1
                                        </div>
                                        <span className="ml-2">Cart Items</span>
                                    </div>
                                    <div className="flex-1 h-0.5 mx-4 bg-slate-200"></div>
                                    <div className={`flex items-center ${checkoutStep >= 2 ? (checkoutStep === 2 ? 'text-green-500 font-bold' : 'text-custom-500') : 'text-slate-400'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${checkoutStep >= 2 ? (checkoutStep === 2 ? 'border-green-500' : 'border-custom-500') : 'border-slate-400'}`}>
                                            2
                                        </div>
                                        <span className="ml-2">Order Summary</span>
                                    </div>
                                    <div className="flex-1 h-0.5 mx-4 bg-slate-200"></div>
                                    <div className={`flex items-center ${checkoutStep >= 3 ? (checkoutStep === 3 ? 'text-green-500 font-bold' : 'text-custom-500') : 'text-slate-400'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${checkoutStep >= 3 ? (checkoutStep === 3 ? 'border-green-500' : 'border-custom-500') : 'border-slate-400'}`}>
                                            3
                                        </div>
                                        <span className="ml-2">Order Confirmation</span>
                                    </div>
                                </div>

                                {/* Step Content */}
                                <div className="max-h-[60vh] overflow-y-auto">
                                    {checkoutStep === 1 && (
                                        <div>
                                            <div className="flex items-center gap-2 mb-4">
                                                <div className="flex-1">
                                                    <input
                                                        ref={searchInputRef}
                                                        type="text"
                                                        className="form-input uppercase w-full"
                                                        placeholder="SCAN OR ENTER SKU..."
                                                        value={searchQuery}
                                                        onChange={(e) => {
                                                            setSearchQuery(e.target.value.toUpperCase());
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                quantityInputRef.current?.focus();
                                                            }
                                                        }}
                                                        disabled={isLoading}
                                                        style={{ textTransform: 'uppercase' }}
                                                    />
                                                </div>
                                                <div className="w-24">
                                                    <input
                                                        ref={quantityInputRef}
                                                        type="number"
                                                        min="1"
                                                        className="form-input w-full text-center"
                                                        placeholder="Qty"
                                                        value={quantity}
                                                        onChange={(e) => {
                                                            setQuantity(e.target.value);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleSearch();
                                                            }
                                                        }}
                                                        disabled={isLoading}
                                                    />
                                                </div>
                                                <button
                                                    className="btn bg-custom-500 text-white"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleSearch();
                                                    }}
                                                    disabled={isSearching || isLoading}
                                                >
                                                    {isSearching ? (
                                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                                    ) : (
                                                        <Search className="h-4 w-4" />
                                                    )}
                                                </button>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="bg-slate-100 dark:bg-zink-600">
                                                            <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-zink-200 w-[35%]">Product</th>
                                                            <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">
                                                                Price
                                                                <span className="ml-1 text-xs text-slate-400" title="Click the edit icon to modify prices">ⓘ</span>
                                                            </th>
                                                            <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Discount</th>
                                                            <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Net Price</th>
                                                            <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Qty</th>
                                                            <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Amount</th>
                                                            <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 dark:text-zink-200 w-[5%]"></th>
                                                        </tr>
                                                    </thead>
                                                </table>
                                                <div className="space-y-3 mt-3">
                                                    {cartItems.map((item) => {
                                                        const unitPrice = parseFloat(item?.unitPrice || '0');
                                                        const discountValue = parseFloat(item?.discountValue || '0');
                                                        const netPrice = unitPrice - discountValue;
                                                        const totalAmount = netPrice * (item?.quantity || 0) * (item?.packOf || 0);
                                                        
                                                        return (
                                                            <div key={`cart-item-${item?.variantId}`} className="bg-slate-50 dark:bg-zink-700 rounded-lg shadow-sm hover:bg-slate-100 dark:hover:bg-zink-600 transition-colors">
                                                                <table className="w-full table-fixed">
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="px-4 py-3 rounded-l-lg w-[35%]">
                                                                                <div className="flex flex-col">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className="px-2 py-0.5 text-xs font-medium bg-custom-100 text-custom-500 rounded whitespace-nowrap">
                                                                                            {item?.sku || 'N/A'}
                                                                                        </span>
                                                                                        <span className="text-xs text-slate-500 whitespace-nowrap">
                                                                                            {item?.sizeNames || 'N/A'} | Pack Of: {item?.packOf || 0} | 
                                                                                            <span className={`ml-1 ${item?.inventory > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                                                                Stock: {item?.inventory || 0}
                                                                                            </span>
                                                                                        </span>
                                                                                    </div>
                                                                                    <h6 className="text-sm font-medium truncate">
                                                                                        {item?.productName || 'N/A'}
                                                                                    </h6>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right w-[10%]">
                                                                                {editingPriceId === item?.variantId ? (
                                                                                    <div className="flex items-center gap-1">
                                                                                                           <input
                       type="number"
                       step="0.01"
                       min="0"
                       className="form-input text-sm w-24 text-right"
                       value={editingPriceValue}
                       onChange={(e) => setEditingPriceValue(e.target.value)}
                       onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                               e.preventDefault();
                               handlePriceSave(item?.variantId || 0);
                           } else if (e.key === 'Escape') {
                               e.preventDefault();
                               handlePriceCancel();
                           }
                       }}
                       autoFocus
                   />
                                                                                                                                                                                 <button
                                                                                             className="btn btn-xs bg-green-500 text-white p-0.5"
                                                                                             onClick={() => handlePriceSave(item?.variantId || 0)}
                                                                                             type="button"
                                                                                         >
                                                                                             ✓
                                                                                         </button>
                                                                                         <button
                                                                                             className="btn btn-xs bg-red-500 text-white p-0.5"
                                                                                             onClick={() => handlePriceCancel()}
                                                                                             type="button"
                                                                                         >
                                                                                             ✕
                                                                                         </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="flex items-center justify-end gap-1">
                                                                                        <span className="text-sm font-medium">
                                                                                            {unitPrice.toFixed(2)}
                                                                                        </span>
                                                                                        <button
                                                                                            className="btn btn-xs bg-slate-200 hover:bg-slate-300 p-0.5"
                                                                                            onClick={() => handlePriceEdit(item?.variantId || 0, item?.unitPrice || '0')}
                                                                                            title="Edit Price"
                                                                                        >
                                                                                            ✎
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right w-[10%]">
                                                                                <span className="text-sm text-green-500 inline-block w-full text-right">-{discountValue.toFixed(2)}</span>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right w-[10%]">
                                                                                <span className="text-sm font-medium inline-block w-full text-right">{netPrice.toFixed(2)}</span>
                                                                            </td>
                                                                            <td className="px-4 py-3 w-[10%]">
                                                                                <div className="flex items-center justify-end gap-0.5">
                                                                                    <button
                                                                                        className="btn btn-sm bg-slate-200 hover:bg-slate-300 p-1"
                                                                                        onClick={() => updateQuantity(item?.variantId || 0, false)}
                                                                                    >
                                                                                        <Minus className="h-3 w-3" />
                                                                                    </button>
                                                                                    <span className="min-w-[1.5rem] text-center font-medium">
                                                                                        {item?.quantity || 0}
                                                                                    </span>
                                                                                    <button
                                                                                        className="btn btn-sm bg-slate-200 hover:bg-slate-300 p-1"
                                                                                        onClick={() => updateQuantity(item?.variantId || 0, true)}
                                                                                    >
                                                                                        <Plus className="h-3 w-3" />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right w-[10%]">
                                                                                <span className="text-sm font-medium inline-block w-full text-right">{totalAmount.toFixed(2)}</span>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center rounded-r-lg w-[5%]">
                                                                                <button
                                                                                    className="btn btn-sm bg-red-500 text-white p-1"
                                                                                    onClick={() => removeFromCart(item?.variantId || 0)}
                                                                                >
                                                                                    <Trash2 className="h-3 w-3" />
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {checkoutStep === 2 && (
                                        <div>
                                            {/* Shipping Address Selection */}
                                            <div className="mb-6">
                                                <h4 className="text-sm font-medium mb-3">Select Shipping Address</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {addresses.map((address) => (
                                                        <div
                                                            key={`address-${address.id}`}
                                                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                                                selectedAddress === address.id
                                                                    ? 'border-custom-500 bg-custom-50 dark:bg-custom-900/20'
                                                                    : 'border-slate-200 dark:border-zink-500 hover:border-custom-500'
                                                            }`}
                                                            onClick={() => setSelectedAddress(address.id)}
                                                        >
                                                            <div className="flex items-start justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                                                                        address.addressType === 'Billing'
                                                                            ? 'bg-blue-100 text-blue-600'
                                                                            : 'bg-purple-100 text-purple-600'
                                                                    }`}>
                                                                        {address.addressType}
                                                                    </span>
                                                                </div>
                                                                {selectedAddress === address.id && (
                                                                    <div className="w-5 h-5 rounded-full border-2 border-custom-500 flex items-center justify-center">
                                                                        <div className="w-3 h-3 rounded-full bg-custom-500"></div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="space-y-1 text-sm">
                                                                <p className="font-medium">{address.addressLine1}</p>
                                                                {address.addressLine2 && <p>{address.addressLine2}</p>}
                                                                {address.landmark && <p className="text-slate-500">Landmark: {address.landmark}</p>}
                                                                <p>{address.city}, {address.district}</p>
                                                                <p>{address.state}, {address.country} - {address.pinCode}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {!selectedAddress && (
                                                    <div className="mt-4 text-red-500 text-sm">
                                                        Please select a shipping address to continue
                                                    </div>
                                                )}
                                            </div>

                                                                        {/* Order Items */}
                            <div className="mb-6">
                                <h4 className="text-sm font-medium mb-3">Cart Items</h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-100 dark:bg-zink-600">
                                                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-zink-200 w-[35%]">Product</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Price</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Discount</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Net Price</th>
                                                <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Qty</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-zink-200 w-[10%]">Amount</th>
                                            </tr>
                                        </thead>
                                    </table>
                                    <div className="space-y-3 mt-3">
                                        {cartItems.map((item) => {
                                            const unitPrice = parseFloat(item?.unitPrice || '0');
                                            const discountValue = parseFloat(item?.discountValue || '0');
                                            const netPrice = unitPrice - discountValue;
                                            const totalAmount = netPrice * (item?.quantity || 0) * (item?.packOf || 0);
                                            
                                            return (
                                                <div key={`summary-item-${item?.variantId}`} className="bg-slate-50 dark:bg-zink-700 rounded-lg shadow-sm">
                                                    <table className="w-full table-fixed">
                                                        <tbody>
                                                            <tr>
                                                                <td className="px-4 py-3 rounded-l-lg w-[35%]">
                                                                    <div className="flex flex-col">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="px-2 py-0.5 text-xs font-medium bg-custom-100 text-custom-500 rounded whitespace-nowrap">
                                                                                {item?.sku || 'N/A'}
                                                                            </span>
                                                                            <span className="text-xs text-slate-500 whitespace-nowrap">
                                                                                {item?.sizeNames || 'N/A'} | Pack Of: {item?.packOf || 0} | 
                                                                                <span className={`ml-1 ${item?.inventory > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                                                    Stock: {item?.inventory || 0}
                                                                                </span>
                                                                            </span>
                                                                        </div>
                                                                        <h6 className="text-sm font-medium truncate">
                                                                            {item?.productName || 'N/A'}
                                                                        </h6>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-right w-[10%]">
                                                                    <span className="text-sm font-medium">
                                                                        {unitPrice.toFixed(2)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-right w-[10%]">
                                                                    <span className="text-sm text-green-500 inline-block w-full text-right">-{discountValue.toFixed(2)}</span>
                                                                </td>
                                                                <td className="px-4 py-3 text-right w-[10%]">
                                                                    <span className="text-sm font-medium inline-block w-full text-right">{netPrice.toFixed(2)}</span>
                                                                </td>
                                                                <td className="px-4 py-3 text-center w-[10%]">
                                                                    <span className="text-sm font-medium">
                                                                        {item?.quantity || 0}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-right rounded-r-lg w-[10%]">
                                                                    <span className="text-sm font-medium inline-block w-full text-right">{totalAmount.toFixed(2)}</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                                        </div>
                                    )}

                                    {checkoutStep === 3 && orderDetails && (
                                        <div className="text-center py-8">
                                            <div className="mb-6">
                                                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                                <h3 className="text-lg font-semibold mb-2">Order Placed Successfully!</h3>
                                                <p className="text-slate-500 mb-4">
                                                    Your order has been confirmed and is being processed.
                                                </p>
                                                <div className="bg-slate-50 dark:bg-zink-600 rounded-lg p-4 inline-block">
                                                    <div className="text-sm text-slate-500 mb-1">Order Number</div>
                                                    <div className="text-2xl font-bold text-custom-500">
                                                        {orderDetails.orderId}
                                                    </div>
                                                    <div className="text-sm text-slate-500 mt-1">
                                                        {new Date(orderDetails.orderDate).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                                <button
                                                    className="btn bg-custom-500 text-white inline-flex items-center"
                                                    onClick={handleViewOrderDetails}
                                                >
                                                    <Receipt className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    <span>View Order Details</span>
                                                </button>
                                                <button
                                                    className="btn bg-green-500 text-white inline-flex items-center"
                                                    onClick={handleDownloadInvoice}
                                                >
                                                    <Download className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    <span>Download Invoice</span>
                                                </button>
                                                <button
                                                    className="btn bg-green-600 text-white inline-flex items-center"
                                                    onClick={handleShareInvoiceOnWhatsApp}
                                                    title="Share invoice on WhatsApp"
                                                >
                                                    <MessageCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    <span>Share on WhatsApp</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Cart Summary */}
                            <div className="col-span-3">
                                <div className="bg-slate-50 dark:bg-zink-600 rounded-lg p-4 sticky top-4">
                                    <h6 className="text-15 font-medium mb-4">Cart Summary</h6>
                                    <div className="space-y-3">
                                        {/* Cart Totals */}
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Items ({cartSummary?.totalProducts ?? localSummary.totalProducts})</span>
                                                <span className="font-medium">₹{(cartSummary?.subTotal ?? localSummary.subTotal).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Item Discounts</span>
                                                <span className="text-green-500">-₹{(cartSummary?.totalDiscount ?? localSummary.totalDiscount).toFixed(2)}</span>
                                            </div>
                                            {checkoutStep >= 2 && (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-slate-500">GST ({taxRate}%)</span>
                                                        <span>₹{(cartSummary?.gst ?? localSummary.gst).toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-slate-500">Shipping</span>
                                                        <span>₹{(checkoutStep === 3 ? 0 : cartSummary?.shipping ?? localSummary.shipping).toFixed(2)}</span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="border-t border-slate-200 dark:border-zink-500 pt-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium">{checkoutStep >= 2 ? 'Total Amount' : 'Subtotal'}</span>
                                                    <span className="text-lg font-semibold">₹{(checkoutStep === 3 ? 0 : cartSummary?.total ?? localSummary.total).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="mt-4 space-y-2">
                                            {checkoutStep === 3 ? (
                                                <button
                                                    className="btn bg-custom-500 text-white w-full py-2"
                                                    onClick={handleNewOrder}
                                                >
                                                    Place New Order
                                                </button>
                                            ) : (
                                                <div className="flex gap-2">
                                                    {checkoutStep > 1 && (
                                                        <button
                                                            className="btn bg-slate-200 hover:bg-slate-300 text-slate-700 flex-1 py-2"
                                                            onClick={handlePrevStep}
                                                        >
                                                            Previous
                                                        </button>
                                                    )}
                                                    {checkoutStep === 2 ? (
                                                        <button
                                                            className="btn bg-green-500 text-white flex-1 py-2"
                                                            onClick={handleSubmitOrder}
                                                            disabled={isSubmitting || !selectedAddress}
                                                        >
                                                            {isSubmitting ? (
                                                                <div className="flex items-center justify-center whitespace-nowrap">
                                                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                                    Processing...
                                                                </div>
                                                            ) : (
                                                                'Submit Order'
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn bg-custom-500 text-white flex-1 py-2"
                                                            onClick={handleNextStep}
                                                            disabled={isNextLoading}
                                                        >
                                                            {isNextLoading ? (
                                                                <div className="flex items-center justify-center whitespace-nowrap">
                                                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                                    Processing...
                                                                </div>
                                                            ) : (
                                                                'Checkout'
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default POS; 